import { useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";

type StatusKind =
  | "lead_status"
  | "submission_status"
  | "deal_status"
  | "temperature"
  | "automation_status"
  | "preferred_channel"
  | "direction"
  | "environment"
  | "task_type"
  | "task_priority";

const STATUS_TONE: Record<string, string> = {
  // lead_status
  new: "border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300",
  pending_required_data:
    "border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300",
  qualified:
    "border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300",
  disqualified:
    "border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300",
  converted:
    "border-emerald-400 dark:border-emerald-600 text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-950",
  archived:
    "border-zinc-300 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400",

  // submission_status
  received:
    "border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300",
  spam_suspected:
    "border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300",
  processed:
    "border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300",
  processing_failed:
    "border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950",
  ignored: "border-zinc-300 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400",

  // deal_status
  open: "border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300",
  won: "border-emerald-400 dark:border-emerald-600 text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-950",
  lost: "border-red-300 dark:border-red-700 text-red-700 dark:text-red-300",
  paused:
    "border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300",
  future_nurture:
    "border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300",

  // temperature
  cold: "border-sky-300 dark:border-sky-700 text-sky-700 dark:text-sky-300",
  warm: "border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300",
  hot: "border-red-300 dark:border-red-700 text-red-700 dark:text-red-300",

  // automation_status
  pending:
    "border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300",
  running:
    "border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300",
  success:
    "border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300",
  failed:
    "border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950",
  skipped: "border-zinc-300 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400",
  partial_success:
    "border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300",

  // environment
  production:
    "border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300",
  staging:
    "border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300",
  test: "border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300",
  audit:
    "border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300",

  // task_priority
  low: "border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300",
  medium:
    "border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300",
  high: "border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300",
  urgent:
    "border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950",
};

export const StatusBadge = ({
  value,
  kind,
}: {
  value: string | null | undefined;
  kind: StatusKind;
}) => {
  const translate = useTranslate();
  if (!value) return <span className="text-muted-foreground">—</span>;
  const label = translate(`crm.faromarket.${kind}.${value}`, { _: value });
  const tone =
    STATUS_TONE[value] ??
    "border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300";
  return (
    <Badge variant="outline" className={tone}>
      {label}
    </Badge>
  );
};

export const TestBadge = ({ value }: { value: boolean | null | undefined }) => {
  const translate = useTranslate();
  if (!value) return null;
  return (
    <Badge
      variant="outline"
      className="border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300"
    >
      {translate("resources.leads.fields.is_test", { _: "Teste" })}
    </Badge>
  );
};
